import { useState, useEffect, useCallback } from 'react';
import { arnsService, type ArnsData } from '../services/arnsService';
import type { AntOption, PrimaryNameRequest } from '../types';

export interface UseArnsReturn {
  // Data
  currentPrimaryName: string | null;
  userAntProcessId: string | null;
  antOptions: AntOption[];
  primaryNameRequest: PrimaryNameRequest | null;
  undernames: Record<string, any>;
  
  // Loading states
  loading: boolean;
  requestingPrimaryName: boolean;
  settingUndername: boolean;
  
  // Error states
  error: string;
  
  // Actions
  fetchArnsData: (forceRefresh?: boolean) => Promise<void>;
  requestPrimaryName: (params: { antName?: string; selectedAntProcessId?: string }) => Promise<void>;
  setUndername: (params: { undername: string; url?: string; transactionId?: string }) => Promise<void>;
  clearError: () => void;
  
  // Status messages
  lastRequestResult: { success: boolean; message: string } | null;
}

export function useArns(walletAddress: string | null, customProcess: any): UseArnsReturn {
  // Data state
  const [currentPrimaryName, setCurrentPrimaryName] = useState<string | null>(null);
  const [userAntProcessId, setUserAntProcessId] = useState<string | null>(null);
  const [antOptions, setAntOptions] = useState<AntOption[]>([]);
  const [primaryNameRequest, setPrimaryNameRequest] = useState<PrimaryNameRequest | null>(null);
  const [undernames, setUndernames] = useState<Record<string, any>>({});
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [requestingPrimaryName, setRequestingPrimaryName] = useState(false);
  const [settingUndername, setSettingUndername] = useState(false);
  
  // Error and status
  const [error, setError] = useState('');
  const [lastRequestResult, setLastRequestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Initialize service with custom process
  useEffect(() => {
    if (customProcess) {
      arnsService.setCustomProcess(customProcess);
    }
  }, [customProcess]);

  // Fetch ArNS data
  const fetchArnsData = useCallback(async (forceRefresh = false) => {
    if (!walletAddress) {
      // Reset state when no wallet
      setCurrentPrimaryName(null);
      setUserAntProcessId(null);
      setAntOptions([]);
      setPrimaryNameRequest(null);
      setUndernames({});
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await arnsService.fetchArnsData(walletAddress, forceRefresh);
      
      setCurrentPrimaryName(data.currentPrimaryName);
      setUserAntProcessId(data.userAntProcessId);
      setAntOptions(data.antOptions);
      setPrimaryNameRequest(data.primaryNameRequest);
      
      // Fetch undernames if user has an ANT
      if (data.userAntProcessId) {
        try {
          const records = await arnsService.getAntRecords(data.userAntProcessId);
          setUndernames(records);
        } catch (recordError) {
          console.warn("[useArns] Failed to fetch ANT records:", recordError);
          setUndernames({});
        }
      } else {
        setUndernames({});
      }

    } catch (fetchError: any) {
      console.error("[useArns] Failed to fetch ArNS data:", fetchError);
      setError(fetchError.message || 'Failed to fetch ArNS data');
    } finally {
      setLoading(false);
    }
  }, [walletAddress]);

  // Request primary name
  const requestPrimaryName = useCallback(async (params: { antName?: string; selectedAntProcessId?: string }) => {
    if (!walletAddress) {
      setError('Wallet not connected');
      return;
    }

    setRequestingPrimaryName(true);
    setError('');
    setLastRequestResult(null);

    try {
      const result = await arnsService.requestPrimaryName({
        walletAddress,
        ...params
      });

      setLastRequestResult(result);
      
      if (result.success) {
        // Refresh data after successful request
        await fetchArnsData(true);
      } else {
        setError(result.message);
      }

    } catch (requestError: any) {
      const errorMessage = requestError.message || 'Failed to request primary name';
      setError(errorMessage);
      setLastRequestResult({ success: false, message: errorMessage });
    } finally {
      setRequestingPrimaryName(false);
    }
  }, [walletAddress, fetchArnsData]);

  // Set undername - updated to use new parameter name
  const setUndername = useCallback(async (params: { undername: string; url?: string; transactionId?: string }) => {
    if (!userAntProcessId) {
      setError('No ANT available to set undername');
      return;
    }

    setSettingUndername(true);
    setError('');

    try {
      const result = await arnsService.setUndernameRecord({
        antProcessId: userAntProcessId,
        ...params
      });

      if (result.success) {
        // Refresh undernames after successful update
        const records = await arnsService.getAntRecords(userAntProcessId);
        setUndernames(records);
      } else {
        setError(result.message);
      }

    } catch (undernameError: any) {
      setError(undernameError.message || 'Failed to set undername');
    } finally {
      setSettingUndername(false);
    }
  }, [userAntProcessId]);

  // Clear error
  const clearError = useCallback(() => {
    setError('');
    setLastRequestResult(null);
  }, []);

  // Auto-fetch when wallet address changes
  useEffect(() => {
    fetchArnsData();
  }, [fetchArnsData]);

  return {
    // Data
    currentPrimaryName,
    userAntProcessId,
    antOptions,
    primaryNameRequest,
    undernames,
    
    // Loading states
    loading,
    requestingPrimaryName,
    settingUndername,
    
    // Error states
    error,
    
    // Actions
    fetchArnsData,
    requestPrimaryName,
    setUndername,
    clearError,
    
    // Status
    lastRequestResult
  };
}